import { useMemo, useState } from 'react';
import { color } from '../styles/theme';
import { shortDate } from '../lib/format';
import { makeLookup, summaryOf } from '../lib/presenters';
import {
  acceptStatus,
  endorseCopy,
  isInbox,
  INBOX_STATUSES,
  studyKeysOf,
  studySlugOf,
  statusLabelOf,
} from '../lib/pipeline';
import { useMeta, useOverview, useSubmissions, useSetStatus, useSetStudy } from '../api/queries';
import { useEndorse } from '../api/endorsements';
import type { SubmissionRow } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { SectionTabs } from '../components/SectionTabs';
import {
  PageTitle,
  Card,
  Chip,
  ColHead,
  Confirm,
  Loading,
  ErrorNote,
  Empty,
  RedactedBadge,
  StatusNote,
  pillSelectStyle,
} from '../components/ui';
import { IconFunnel } from '../components/icons';
import { EndorseControl } from '../components/EndorseControl';
import { ParticipantDetail } from './ParticipantDetail';

/**
 * Forms submission — the inbox for the four participant pathways.
 *
 * Every intake, referral, waitlist and early-interest form lands here first,
 * and stays here while nobody has accepted it: Received, Waitlist and Early
 * interest are the three statuses that mean "not in a study yet". Accepting a
 * row moves it to the first column of that study's board, which is where it
 * becomes a participant.
 *
 * The rows are the same four lists the boards pull, asked for by status so
 * the API hands back only the inbox. A row that arrived without a study — a
 * referral may leave it blank — or a waitlist sign-up that named several gets
 * a picker instead of Accept: the coordinator sets the study first, through
 * PATCH /submissions/{id}/study, and only then can accept.
 *
 * Endorse to…, beside Accept, is the other way out of the inbox: the row is
 * handed to another study's team instead, and leaves here for that board's
 * Newly endorsed column. It is the same control as the record's and the
 * board's, and needs a study to hand over from just as Accept does.
 */

export type FormsTab = 'all' | 'intake' | 'referral' | 'waitlist' | 'early-interest';

const BOARD_PAGE_SIZE = 100;

const GRID = 'minmax(0,2fr) minmax(0,1fr) minmax(0,1.3fr) minmax(0,1fr) 70px 370px';

/** The one question open on the table: a row's Accept, or its Endorse to a study. */
type Pending = { id: string; action: 'accept' } | { id: string; action: 'endorse'; study: string };

export function FormsSubmission({
  tab,
  onTab,
}: {
  tab: FormsTab;
  onTab: (next: FormsTab) => void;
}) {
  const meta = useMeta();
  const overview = useOverview();
  const { can } = useAuth();
  const setStatus = useSetStatus();
  const setStudy = useSetStudy();
  const endorse = useEndorse();
  const [studyFilter, setStudyFilter] = useState<string>('all');
  const [selected, setSelected] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<Pending | null>(null);
  // What the last hand-off did, said under the table: the row leaves this
  // list, and nothing else here would show where the person went.
  const [endorsed, setEndorsed] = useState<string | null>(null);

  const base = {
    status: 'all',
    q: '',
    sort: 'newest' as const,
    page: 1,
    pageSize: BOARD_PAGE_SIZE,
    statuses: INBOX_STATUSES.join(','),
  };
  const intake = useSubmissions({ ...base, tab: 'intake' });
  const referral = useSubmissions({ ...base, tab: 'referral' });
  const waitlist = useSubmissions({ ...base, tab: 'waitlist' });
  const interest = useSubmissions({ ...base, tab: 'early-interest' });
  const lists = [intake, referral, waitlist, interest];

  /** Rows waiting per form — received, waitlist or early interest — the same counts the sidebar badge is built from. */
  const fresh = useMemo(() => {
    const stats = overview.data?.formStats ?? [];
    const of = (key: string) => stats.find((f) => f.key === key)?.fresh ?? 0;
    const each = {
      intake: of('intake'),
      referral: of('referral'),
      waitlist: of('waitlist'),
      'early-interest': of('early-interest'),
    };
    return { ...each, all: each.intake + each.referral + each.waitlist + each['early-interest'] };
  }, [overview.data]);

  if (meta.isLoading || lists.some((l) => l.isLoading && !l.data)) return <Loading />;
  const failed = lists.find((l) => l.error);
  if (failed) return <ErrorNote message={(failed.error as Error).message} />;
  if (!meta.data) return null;

  const L = makeLookup(meta.data);
  const rows: SubmissionRow[] = lists.flatMap((l) => l.data?.rows ?? []).filter(isInbox);
  const truncated = lists.some((l) => l.data && l.data.total > l.data.rows.length);
  const canUpdate = can('submissions:update');

  // Accepting means the first column of a study's board — whatever the API
  // says that is, rather than a status key written here.
  const acceptTo = acceptStatus(meta.data);

  const presentStudies = meta.data.studies.filter((s) => rows.some((r) => studySlugOf(r) === s.key));
  const anyWithoutStudy = rows.some((r) => studySlugOf(r) === null);

  const filtered = rows
    .filter((r) => tab === 'all' || r.form === tab)
    .filter((r) => {
      if (studyFilter === 'all') return true;
      const slug = studySlugOf(r);
      return studyFilter === 'none' ? slug === null : slug === studyFilter;
    })
    .sort((a, b) => (a.createdAt < b.createdAt ? 1 : a.createdAt > b.createdAt ? -1 : 0));

  function accept(row: SubmissionRow) {
    if (!acceptTo) return;
    setConfirming(null);
    setEndorsed(null);
    setStatus.mutate({ id: row.id, status: acceptTo.key });
  }

  // The response names the study from the registry, so the note can say
  // where they went even when it is a study the caller does not work on.
  function handOver(row: SubmissionRow, study: string) {
    setConfirming(null);
    setEndorsed(null);
    endorse.mutate(
      { id: row.id, study },
      { onSuccess: (res) => setEndorsed(endorseCopy(meta.data!, row.name, res.endorsement.to).done) },
    );
  }

  const canPick = canUpdate && meta.data.studies.length > 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }} className="fade-in">
      <PageTitle sub="Intake, referral, waitlist and early-interest forms that have not been accepted into a study yet.">
        Forms submission
      </PageTitle>

      <SectionTabs
        value={tab}
        onChange={onTab}
        tabs={[
          { key: 'all', label: 'All', badge: fresh.all },
          { key: 'intake', label: meta.data.forms.intake.short, badge: fresh.intake },
          { key: 'referral', label: meta.data.forms.referral.short, badge: fresh.referral },
          { key: 'waitlist', label: meta.data.forms.waitlist.short, badge: fresh.waitlist },
          {
            key: 'early-interest',
            label: meta.data.forms['early-interest'].short,
            badge: fresh['early-interest'],
          },
        ]}
      />

      {truncated && (
        <ErrorNote
          message={`Showing the first ${BOARD_PAGE_SIZE} waiting rows of each form. Accept or set aside some to see the rest.`}
        />
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ color: color.muted, display: 'flex' }}>
          <IconFunnel />
        </span>
        <span style={{ fontSize: 12.5, fontWeight: 700, color: color.muted }}>Study:</span>
        <Chip label="All studies" active={studyFilter === 'all'} onClick={() => setStudyFilter('all')} />
        {presentStudies.map((s) => (
          <Chip
            key={s.key}
            label={s.short}
            title={s.label}
            active={studyFilter === s.key}
            onClick={() => setStudyFilter(s.key)}
          />
        ))}
        {anyWithoutStudy && (
          <Chip label="No study" active={studyFilter === 'none'} onClick={() => setStudyFilter('none')} />
        )}
      </div>

      <Card pad={0} style={{ overflow: 'hidden' }}>
        <div className="x-scroll">
          <div style={{ minWidth: 1020 }}>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: GRID,
                gap: 12,
                padding: '11px 20px',
                background: color.bg,
              }}
            >
              <ColHead>Name</ColHead>
              <ColHead>Form</ColHead>
              <ColHead>Study</ColHead>
              <ColHead>Status</ColHead>
              <ColHead>Sent</ColHead>
              <span />
            </div>

            {filtered.map((r) => {
              const fm = meta.data!.forms[r.form];
              const slug = studySlugOf(r);
              const keys = studyKeysOf(r);
              // A sign-up that named several studies keeps the picker beside
              // the one the column holds, so the choice can be changed.
              const needsPick = slug === null || keys.length > 1;
              const studyText = slug === null ? 'No study' : L.studyShort(slug);
              const acceptOpen = confirming?.id === r.id && confirming.action === 'accept';
              const endorseTo =
                confirming?.id === r.id && confirming.action === 'endorse' ? confirming.study : null;
              return (
                <div
                  key={r.id}
                  onClick={() => setSelected(r.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setSelected(r.id);
                    }
                  }}
                  className="hov-row"
                  style={{
                    display: 'grid',
                    gridTemplateColumns: GRID,
                    gap: 12,
                    alignItems: 'center',
                    padding: '13px 20px',
                    borderBottom: `1px solid ${color.line}`,
                    cursor: 'pointer',
                  }}
                >
                  <span style={{ minWidth: 0 }}>
                    <span
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 7,
                        fontSize: 13.5,
                        fontWeight: 700,
                        color: color.ink,
                        minWidth: 0,
                      }}
                    >
                      <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {r.name}
                      </span>
                      {r.redacted && <RedactedBadge />}
                    </span>
                    <span
                      style={{
                        display: 'block',
                        fontSize: 11.5,
                        color: color.muted,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {summaryOf(r, L)}
                    </span>
                  </span>
                  <span style={{ fontSize: 12.5, color: color.ink, minWidth: 0 }}>{fm.short}</span>
                  <span style={{ minWidth: 0 }} onClick={(e) => e.stopPropagation()}>
                    {needsPick && canPick ? (
                      <select
                        value={slug ?? ''}
                        aria-label={`${r.name}: study`}
                        title={
                          keys.length > 1
                            ? `They named ${keys.map((k) => L.studyLabel(k)).join(', ')}. Pick the one to accept them into.`
                            : 'This form arrived without a study. Pick one to accept them into.'
                        }
                        disabled={setStudy.isPending}
                        onChange={(e) => {
                          if (e.target.value) setStudy.mutate({ id: r.id, study: e.target.value });
                        }}
                        style={pillSelectStyle}
                      >
                        <option value="">Pick a study…</option>
                        {meta.data!.studies.map((s) => (
                          <option key={s.key} value={s.key}>
                            {s.short}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span
                        title={keys.length > 1 ? keys.map((k) => L.studyLabel(k)).join(', ') : undefined}
                        style={{
                          display: 'inline-block',
                          maxWidth: '100%',
                          background: color.tint2,
                          color: color.blueDeep,
                          fontSize: 10.5,
                          fontWeight: 800,
                          padding: '3px 10px',
                          borderRadius: 999,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {studyText}
                      </span>
                    )}
                  </span>
                  <span style={{ fontSize: 12.5, fontWeight: 700, color: color.muted, minWidth: 0 }}>
                    {statusLabelOf(meta.data!, r.status)}
                    {/* Who has it, as set from the record's footer. */}
                    {r.ownerName && (
                      <span
                        title={`Owner: ${r.ownerName}`}
                        style={{
                          display: 'block',
                          fontSize: 11,
                          fontWeight: 500,
                          marginTop: 2,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        Owner: {r.ownerName}
                      </span>
                    )}
                  </span>
                  <span style={{ fontSize: 12, color: color.muted }}>{shortDate(r.createdAt)}</span>
                  {/*
                    Both controls sit in a row that opens the record on click
                    and on Enter, so neither event may reach it: a select
                    opened from the keyboard would otherwise put the record
                    over the question it was about to ask.
                  */}
                  <span
                    style={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      alignItems: 'center',
                      gap: 6,
                      flexWrap: 'wrap',
                    }}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                  >
                    {canUpdate && acceptTo && (
                      <Confirm
                        open={acceptOpen}
                        title={`Accept ${r.name} into ${slug ? L.studyShort(slug) : 'the study'}?`}
                        detail={
                          acceptTo.blurb
                            ? `They move to ${acceptTo.label}, and will be shown: “${acceptTo.blurb}”`
                            : `They move to ${acceptTo.label}.`
                        }
                        confirmLabel="Accept"
                        align="right"
                        onConfirm={() => accept(r)}
                        onCancel={() => setConfirming(null)}
                      >
                        <button
                          onClick={() => setConfirming(acceptOpen ? null : { id: r.id, action: 'accept' })}
                          disabled={slug === null || setStatus.isPending}
                          title={slug === null ? 'Pick a study first.' : undefined}
                          className={slug === null ? undefined : 'hov-tint'}
                          style={{
                            background: acceptOpen ? color.tint : 'transparent',
                            border: `1px solid ${color.blue}`,
                            color: color.blue,
                            fontSize: 11.5,
                            fontWeight: 700,
                            padding: '6px 13px',
                            borderRadius: 999,
                            cursor: slug === null ? 'not-allowed' : 'pointer',
                            opacity: slug === null ? 0.5 : 1,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          Accept → {acceptTo.label}
                        </button>
                      </Confirm>
                    )}
                    {canUpdate && (
                      <EndorseControl
                        row={r}
                        meta={meta.data!}
                        size="pill"
                        align="right"
                        target={endorseTo}
                        onTarget={(s) =>
                          setConfirming(s ? { id: r.id, action: 'endorse', study: s } : null)
                        }
                        onEndorse={(study) => handOver(r, study)}
                        pending={endorse.isPending || setStatus.isPending}
                      />
                    )}
                  </span>
                </div>
              );
            })}

            {filtered.length === 0 && <Empty>Nothing waiting. New forms land here first.</Empty>}
          </div>
        </div>
      </Card>

      {endorsed && <StatusNote>{endorsed}</StatusNote>}
      {(setStatus.error || setStudy.error || endorse.error) && (
        <ErrorNote
          message={((setStatus.error ?? setStudy.error ?? endorse.error) as Error).message}
        />
      )}

      {selected && <ParticipantDetail id={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}
